import { Heading, Span, TextInput, Button } from '@pandiafi/design-system'
import { Formik, Field, FieldProps, Form } from 'formik'
import { t } from 'i18next'
import React from 'react'

import { validatePasswords } from './utils'
import { PasswordRequirementSection } from './PasswordRequirementSection'
import { PageTranslationSafeLabel } from '../../components'

import FormFieldWrapper from 'components/FormField'

interface CreatePasswordFormValues {
  newPassword: string
  newPasswordAgain: string
}

export const CreatePasswordForm: React.FC<{
  onCancel: () => void
  onSuccess: (newPassword: string) => void
  customHeader?: string
  customDescription?: string
  customSubmitButtonText?: string
  showCancel?: boolean
}> = ({
  onCancel,
  onSuccess,
  customHeader,
  customDescription,
  customSubmitButtonText,
  showCancel = true,
}) => {
  const handleSubmit = (values: CreatePasswordFormValues) => {
    onSuccess(values.newPassword)
  }

  const handleValidate = (values: CreatePasswordFormValues) => {
    const validationResult = validatePasswords(
      values.newPassword,
      values.newPasswordAgain
    )

    if (
      !validationResult.allRequirementsMet ||
      !validationResult.passwordsMatch
    ) {
      return {
        newPassword: validationResult.allRequirementsMet
          ? undefined
          : t('registration:createNewPasswordValidationErrorGeneric'),
        newPasswordAgain: validationResult.passwordsMatch
          ? undefined
          : t('registration:createNewPasswordValidationErrorMatcingPasswords'),
      }
    }

    return {}
  }

  return (
    <Formik
      initialValues={{
        newPassword: '',
        newPasswordAgain: '',
      }}
      onSubmit={handleSubmit}
      validate={handleValidate}
      validateOnMount
    >
      {({ values, isValid, isSubmitting }) => (
        <Form>
          <Heading element="h2" size="m" className="mb-2">
            {customHeader || t('registration:createNewPassword')}
            {/* todo update new tenant email if changed */}
          </Heading>

          <Span light>
            {customDescription ||
              t('registration:createNewPasswordDescription')}
          </Span>

          <div className="flex flex-col gap-m">
            <div>
              <Field name="newPassword">
                {({
                  field,
                  meta,
                }: FieldProps<CreatePasswordFormValues['newPassword']>) => (
                  <FormFieldWrapper
                    name={field.name}
                    meta={meta}
                    label={t('registration:newPasswordLabel')}
                  >
                    <TextInput {...field} type="password" />
                  </FormFieldWrapper>
                )}
              </Field>

              <Field name="newPasswordAgain">
                {({
                  field,
                  meta,
                }: FieldProps<
                  CreatePasswordFormValues['newPasswordAgain']
                >) => (
                  <FormFieldWrapper
                    name={field.name}
                    meta={meta}
                    label={t('registration:newPasswordAgainLabel')}
                  >
                    <TextInput {...field} type="password" />
                  </FormFieldWrapper>
                )}
              </Field>
            </div>

            <Button
              disabled={!isValid || isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              <PageTranslationSafeLabel>
                {customSubmitButtonText || t('common:save')}
              </PageTranslationSafeLabel>
            </Button>

            {showCancel && (
              <Button type="button" variant="cancel" onClick={() => onCancel()}>
                <PageTranslationSafeLabel>
                  {t('common:cancel')}
                </PageTranslationSafeLabel>
              </Button>
            )}
          </div>

          <PasswordRequirementSection
            newPassword={values.newPassword}
            newPasswordAgain={values.newPasswordAgain}
          />
        </Form>
      )}
    </Formik>
  )
}
